import { api, IIntegrationTokenSetDTO, IUserGenerated, User } from "@/logic/api";
import { useDate } from "@/utility/useDate";
import { SessionContext } from "@/utility/useSession";
import { useContext, useEffect, useState } from "react";
import NotchedCard from "../cards/NotchedCard/NotchedCard";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import KeyIcon from "@mui/icons-material/Key";
import KeyOffIcon from "@mui/icons-material/KeyOff";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CopyIconButton from "../common/CopyIconButton";

const ServiceUserTokenManagement: React.FunctionComponent<{
  generatedUser?: IUserGenerated | null;
  onClose: () => void;
  user?: User | null;
}> = ({ generatedUser, onClose, user }) => {
  const { current: session } = useContext(SessionContext);
  const { localizeDate } = useDate();
  const { t } = useTranslation();

  const userName = generatedUser?.user?.userName ?? user?.userName ?? "";

  const getServiceUserCredentials = api.hooks.admin.listCredentials();
  const integrationTokenAPIcreate = api.hooks.integrationToken.createToken();
  const integrationTokenAPIrevoke = api.hooks.integrationToken.revokeToken();
  const integrationTokenAPIcreateSet = api.hooks.integrationToken.createSet();
  const integrationTokenAPIfetchSet = api.hooks.integrationToken.getSetByCredentialUuid();

  const [newToken, setNewToken] = useState<string>();
  const [credentialUuids, setCredentialUuids] = useState<string[]>([]);
  const [integrationTokenSets, setIntegrationTokenSets] = useState<IIntegrationTokenSetDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<{ setUuid: string; tokenUuid: string; tokenKey: string } | null>(null);

  useEffect(() => {
    const filter = getFilter();
    setIsLoading(true);

    getServiceUserCredentials.send(session.context, { filter }, (result) => {
      if (result && result.length > 0) {
        const resultCredentialUuids = result.map(credential => credential.uuid);
        setCredentialUuids(resultCredentialUuids);

        resultCredentialUuids.forEach(credentialUuid => {
          getCredentialIntegrationTokenSets(credentialUuid);
        });
      } else {
        setIsLoading(false);
      }
    });
  }, [userName]);

  const getFilter = () => {
    if (generatedUser) {
      return generatedUser.user.userName;
    }

    if (user) {
      return user.userName;
    }
  }

  const getCredentialIntegrationTokenSets = (credentialUuid: string) => {
    if (generatedUser && session.site && integrationTokenSets.length === 0) {
      createCredentialIntegrationTokenSet(credentialUuid);
    } else {
      integrationTokenAPIfetchSet.send(session.context, { uuid: credentialUuid }, (result) => {
        if (result) {
          setIntegrationTokenSets([result]);
          setIsLoading(false);
        } else {
          createCredentialIntegrationTokenSet(credentialUuid);
        }
      });
    }
  }

  const createCredentialIntegrationTokenSet = (credentialId: string) => {
    integrationTokenAPIcreateSet.send(session.context, {
      credential: credentialId,
      tenant: session.site?.tenant.id ?? "",
      scopes: ["service"],
      title: "title"
    }, (result) => {
      if (result) {
        setIntegrationTokenSets([result]);
      }
      setIsLoading(false);
    });
  };

  const createIntegrationToken = (integrationTokenSetUuid: string) => {
    setIsGenerating(true);
    integrationTokenAPIcreate.send(session.context, { integrationTokenSetUuid: integrationTokenSetUuid, expiresAt: null }, (result) => {
      if (result) {
        getCredentialIntegrationTokenSets(credentialUuids[0]);
        setNewToken(result.token);
      }
      setIsGenerating(false);
    });
  }

  const revokeIntegrationToken = (integrationTokenSetUuid: string, tokenUuid: string) => {
    setIsRevoking(true);
    integrationTokenAPIrevoke.send(session.context, { integrationTokenSetUuid: integrationTokenSetUuid, integrationTokenUuid: tokenUuid, reason: "User requested" }, (result) => {
      if (result) {
        getCredentialIntegrationTokenSets(credentialUuids[0]);
      }
      setIsRevoking(false);
      setTokenToRevoke(null);
    })
    setNewToken("");
  }

  const getValidTokensForSet = (tokenSet: IIntegrationTokenSetDTO) => {
    return tokenSet.tokens.filter(token => {
      if (!token.expiresAt) return true;
      if (token.expiresAt && (new Date().getTime() < token.expiresAt.getTime())) return true;
      return false;
    });
  }

  const getValidTokens = () => {
    if (isLoading) {
      return (
        <NotchedCard style={{ width: "100%", maxWidth: "550px" }} label={t("users.activeTokens")}>
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        </NotchedCard>
      );
    }

    const totalValidTokens = integrationTokenSets.reduce(
      (acc, ts) => acc + getValidTokensForSet(ts).length,
      0
    );
    const maxTokens = 2;

    return <NotchedCard style={{ width: "100%", maxWidth: "550px" }} label={`${t("users.activeTokens")} (${totalValidTokens}/${maxTokens})`}>
      {integrationTokenSets.map(tokenSet => {
        const validTokens = getValidTokensForSet(tokenSet);
        const isAtLimit = validTokens.length >= maxTokens;
        return <Box key={tokenSet.uuid}>
          {validTokens.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                px: 3,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
              data-testid="empty-tokens-state"
            >
              <KeyOffIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} aria-hidden="true" />
              <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
                {t("users.noActiveTokens")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("users.noActiveTokensHint")}
              </Typography>
              <Button
                variant="contained"
                onClick={() => createIntegrationToken(tokenSet.uuid)}
                disabled={isGenerating}
                startIcon={isGenerating ? undefined : <AddIcon />}
                data-testid="generate-new-token-empty"
                sx={{
                  fontWeight: "bold",
                  minWidth: 180,
                  minHeight: 36,
                  backgroundColor: (theme) => theme.palette.brand?.main ?? theme.palette.primary.main,
                  color: (theme) => theme.palette.primary.contrastText,
                  transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.brand?.dark ?? theme.palette.primary.dark,
                  },
                  "&:active": { transform: "scale(0.97)" },
                  "&:focus-visible": { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40` },
                }}
                disableRipple
                disableElevation
              >
                {isGenerating ? <CircularProgress size={20} color="inherit" /> : t("users.generateNewToken")}
              </Button>
            </Box>
          )}
          {validTokens.map(validToken => (
            <Box
              key={validToken.uuid}
              component="article"
              data-testid={`token-item-${validToken.tokenKey}`}
              sx={{
                mb: 2,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                transition: "box-shadow 0.2s ease",
                "&:hover": {
                  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 600,
                    mb: 0.5,
                    wordBreak: "break-all",
                  }}
                >
                  {validToken.tokenKey}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("users.tokenCreatedAt")}{" "}
                  <span title={localizeDate(validToken.createdAt).toFormat("yyyy-MM-dd HH:mm:ss")}>
                    {localizeDate(validToken.createdAt).toFormat("MMM d, yyyy")}
                  </span>
                  {" · "}
                  {validToken.expiresAt ? (
                    <>
                      {t("users.tokenExpiresAt")}{" "}
                      <span title={localizeDate(validToken.expiresAt).toFormat("yyyy-MM-dd HH:mm:ss")}>
                        {localizeDate(validToken.expiresAt).toFormat("MMM d, yyyy")}
                      </span>
                    </>
                  ) : (
                    t("users.tokenNeverExpires")
                  )}
                  {" · "}
                  {tokenSet.scopes.join(", ")}
                </Typography>
              </Box>
              <Tooltip title={t("users.revokeToken")}>
                <IconButton
                  onClick={() => setTokenToRevoke({ setUuid: tokenSet.uuid, tokenUuid: validToken.uuid, tokenKey: validToken.tokenKey })}
                  size="small"
                  aria-label={t("users.revokeToken")}
                  data-testid={`revoke-token-${validToken.tokenKey}`}
                  sx={{
                    color: "#6b7280",
                    "&:hover": {
                      color: "#ef4444",
                      bgcolor: "rgba(239, 68, 68, 0.1)",
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 18 }} aria-hidden="true" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          {validTokens.length > 0 && (
            <Button
              disabled={isGenerating || isAtLimit}
              onClick={() => createIntegrationToken(tokenSet.uuid)}
              variant="text"
              startIcon={isGenerating || isAtLimit ? undefined : <AddIcon />}
              data-testid="generate-new-token"
              sx={{
                mt: 1,
                minWidth: 180,
                minHeight: 36,
                transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                "&:active": { transform: "scale(0.97)" },
                "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
                "&:focus-visible": { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40` },
              }}
              disableRipple
              disableElevation
            >
              {isGenerating ? <CircularProgress size={20} color="inherit" /> : isAtLimit ? t("users.maxTokensReached") : t("users.generateNewToken")}
            </Button>
          )}
        </Box>
      })}
    </NotchedCard>
  }

  return (
    <>
      <DialogTitle id="alert-dialog-slide-title" sx={{ textAlign: "center" }}>
        {t("users.tokenManagement")}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {newToken && (
          <Alert
            severity="success"
            icon={<KeyIcon />}
            sx={{ mb: 3, alignItems: "flex-start", textAlign: "left" }}
            data-testid="new-token-alert"
          >
            <Typography fontWeight="bold" sx={{ mb: 1 }}>
              {t("users.newTokenCreated")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                component="code"
                sx={{
                  fontFamily: "monospace",
                  bgcolor: "action.hover",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  wordBreak: "break-all",
                  fontSize: "0.875rem",
                }}
              >
                {newToken}
              </Typography>
              <CopyIconButton textToCopy={newToken} />
            </Box>
          </Alert>
        )}
        {getValidTokens()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" data-testid="close-token-management">
          {t("general.close")}
        </Button>
      </DialogActions>

      <Dialog
        open={tokenToRevoke !== null}
        onClose={() => setTokenToRevoke(null)}
        aria-labelledby="revoke-dialog-title"
        aria-describedby="revoke-dialog-description"
      >
        <DialogTitle id="revoke-dialog-title">
          {t("users.revokeTokenConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="revoke-dialog-description">
            {t("users.revokeTokenConfirmMessage")}
          </DialogContentText>
          {tokenToRevoke && (
            <Typography
              component="code"
              sx={{
                display: "block",
                fontFamily: "monospace",
                bgcolor: "action.hover",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                mt: 2,
                wordBreak: "break-all",
                fontSize: "0.875rem",
              }}
            >
              {tokenToRevoke.tokenKey}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setTokenToRevoke(null)}
            disabled={isRevoking}
            variant="text"
            sx={{
              transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
              "&:active": { transform: "scale(0.97)" },
              "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
              "&:focus-visible": { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40` },
            }}
            disableRipple
            disableElevation
          >
            {t("general.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (tokenToRevoke) {
                revokeIntegrationToken(tokenToRevoke.setUuid, tokenToRevoke.tokenUuid);
              }
            }}
            color="error"
            variant="contained"
            disabled={isRevoking}
            data-testid="confirm-revoke-token"
            sx={{
              fontWeight: "bold",
              minWidth: 120,
              minHeight: 36,
              transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
              "&:active": { transform: "scale(0.97)" },
              "&:focus-visible": { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40` },
            }}
            disableRipple
            disableElevation
          >
            {isRevoking ? <CircularProgress size={20} color="inherit" /> : t("users.revokeToken")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

}

export default ServiceUserTokenManagement;
